/**
 * AP-37 10. — **die Speicherstelle der Kalkulations-Einstellungen**
 * (`calculationSettings`).
 *
 * Muster `features/projects/lib/projekt-einstellungen.ts`: eine Zeile je Mandant,
 * geschrieben **je Reiter** (`PATCH /calculation-settings/:tab`). Der Server nimmt
 * an einem Reiter nur dessen Felder an; `schreibe` verteilt den Patch nach der
 * Zuordnung unten und reicht den Stand von Aufruf zu Aufruf weiter.
 *
 * Die Zuordnung ist dieselbe wie `CALCULATION_SETTING_FIELDS` im Backend.
 */
import { api } from '@/lib/axios'
import { mitStand, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'

export const REITER_FELDER: Record<string, readonly string[]> = {
  allgemein: [
    'defaultLaborMode',
    'roundingStep',
    'validityDays',
    'quickPhaseKey',
    'quickQualificationCode',
    'priceAgeDays',
    'manualPriceDeviationPercent',
    'allowSubgroups',
    'reportOptionalWithoutPrice',
    'costLineQuantityBasisDefault',
    'workStepQuantityBasisDefault',
    'requestLeadDays',
    'requestMinDays',
    'requestMergeDefault',
    'requestUpdateArticlesDefault',
  ],
  nummernkreis: ['numberPattern', 'numberPrefix', 'numberDigits', 'yearlyReset'],
  verschnitt: ['wasteByArticleGroup'],
  lv: ['bidderInfo'],
}

const REITER_JE_FELD: Record<string, string> = Object.fromEntries(
  Object.entries(REITER_FELDER).flatMap(([reiter, felder]) => felder.map((f) => [f, reiter])),
)

async function schreibe(patch: Datensatz, stand: string | undefined): Promise<Datensatz> {
  const jeReiter = new Map<string, Datensatz>()
  for (const [feld, wert] of Object.entries(patch)) {
    const reiter = REITER_JE_FELD[feld] ?? 'allgemein'
    jeReiter.set(reiter, { ...(jeReiter.get(reiter) ?? {}), [feld]: wert })
  }
  let antwort: Datensatz | undefined
  let standJetzt = stand
  for (const [reiter, teil] of jeReiter) {
    antwort = (await api.patch<Datensatz>(`/calculation-settings/${reiter}`, teil, mitStand(standJetzt))).data
    standJetzt = ((antwort.data as Datensatz | undefined)?.updatedAt as string | undefined) ?? standJetzt
  }
  return antwort ?? (await api.get<Datensatz>('/calculation-settings')).data
}

export function useKalkulationsEinstellungen(daten: Datensatz | null): Speicherstelle<unknown> {
  return useSpeicherstelle<Datensatz>({
    schluessel: 'calculationSettings',
    bezeichnung: 'Einstellungen der Kalkulation',
    daten,
    schreibe,
  }) as Speicherstelle<unknown>
}

/** Stelle einer Listenzeile (Phase, Qualifikation, Regiesatz, Vorlage, Grund). */
export function useZeilenStelle(art: string, url: string, zeile: { id: string }, bezeichnung: string): Speicherstelle<unknown> {
  return useSpeicherstelle<Datensatz>({
    schluessel: `${art}:${zeile.id}`,
    bezeichnung,
    daten: zeile as unknown as Datensatz,
    schreibe: (patch, stand) => api.patch<Datensatz>(url, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url, method: 'PATCH' },
  }) as Speicherstelle<unknown>
}
